import React, { useEffect, useRef, useState } from 'react'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'
import { isShapeTextFrame } from '@/lib/shapes/shape-text-frame'
import { toast } from 'sonner'
import { AlertTriangle } from 'lucide-react'
import { useFonts } from '@/providers/font'
import { collectFontFamilyIdsFromTextBoxes } from '@/lib/fonts/collect-font-family-ids'
import type { OnShapeCommitted } from '../types/on-shape-committed'
import { fallBackTextShapeFonts } from '../handlers/fall-back-text-shape-fonts'

const FONT_FALLBACK_MESSAGE = 'Your fonts were removed and replaced with Helvetica in your book.'

export function MissingFontsChecker({
  shapeManagerRef,
  checkApplied,
  onShapeCommitted,
}: {
  shapeManagerRef: React.RefObject<ShapeManagerRef | null>
  checkApplied: number
  onShapeCommitted?: OnShapeCommitted
}) {
  const { usedFamilies, isUsedCatalogReady, isError: fontsLoadError } = useFonts()
  const hasShownToastRef = useRef(false)
  const [fontAlert, setFontAlert] = useState('')

  useEffect(() => {
    if (fontsLoadError) {
      if (!hasShownToastRef.current) {
        hasShownToastRef.current = true
        toast.error("Fonts couldn't be loaded.", {
          icon: <AlertTriangle className="h-5 w-5 text-red-600" />,
        })
        setFontAlert("Fonts couldn't be loaded.")
      }

      return
    }

    // Only compare against a catalog that actually loaded. Logout clears the query cache while home
    // is still mounted, which empties `usedFamilies` without a loading state; treating that as
    // "every font is missing" showed the fallback toast and rewrote frames to Helvetica.
    if (!isUsedCatalogReady) return
    if (checkApplied === 0) return
    if (hasShownToastRef.current) return

    // Text-unification Phase 2: a text frame is a ShapeManager shape carrying `paragraphs`, not a
    // TextManager box (which never renders any content now — see CLAUDE.md Known Gap #20).
    const textShapes = (shapeManagerRef.current?.getShapes() ?? []).filter(isShapeTextFrame)

    if (textShapes.length === 0) return

    const usedFontFamilyIds = collectFontFamilyIdsFromTextBoxes(textShapes)
    const availableFamilyIds = new Set(usedFamilies.map((f) => f.fontFamilyId))

    const missingFamilyIds = new Set(
      [...usedFontFamilyIds].filter((id) => !availableFamilyIds.has(id))
    )

    if (missingFamilyIds.size > 0) {
      hasShownToastRef.current = true
      toast.error(FONT_FALLBACK_MESSAGE, {
        icon: <AlertTriangle className="h-5 w-5 text-red-600" />,
      })
      setFontAlert('Your fonts were removed and replaced with Helvetica.')

      fallBackTextShapeFonts(textShapes, missingFamilyIds, shapeManagerRef, onShapeCommitted)
    }
  }, [
    checkApplied,
    shapeManagerRef,
    usedFamilies,
    onShapeCommitted,
    isUsedCatalogReady,
    fontsLoadError,
  ])

  return (
    <div role="alert" aria-atomic="true" className="sr-only">
      {fontAlert}
    </div>
  )
}
